import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../App';
import { getCertificate, verifyCertificate } from '../../services/lmsService';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import Footer from '../Footer';
import { Award, Download, Share2, CheckCircle, Calendar, User, BookOpen, ArrowLeft, Search, ExternalLink, Printer, RefreshCw } from 'lucide-react';

// Certificate template configurations
const CERTIFICATE_TEMPLATES = {
  classic: { gradient: 'from-indigo-500 via-purple-500 to-pink-500', accent: 'indigo', iconBg: 'from-indigo-400 to-purple-500', textColor: 'text-indigo-600' },
  elegant: { gradient: 'from-amber-500 via-yellow-500 to-orange-500', accent: 'amber', iconBg: 'from-amber-400 to-orange-500', textColor: 'text-amber-600' },
  modern: { gradient: 'from-blue-500 via-cyan-500 to-teal-500', accent: 'blue', iconBg: 'from-blue-400 to-cyan-500', textColor: 'text-blue-600' },
  professional: { gradient: 'from-gray-700 via-gray-600 to-gray-800', accent: 'gray', iconBg: 'from-gray-600 to-gray-700', textColor: 'text-gray-700' },
  vibrant: { gradient: 'from-rose-500 via-pink-500 to-purple-500', accent: 'rose', iconBg: 'from-rose-400 to-pink-500', textColor: 'text-rose-600' },
  nature: { gradient: 'from-emerald-500 via-green-500 to-teal-500', accent: 'emerald', iconBg: 'from-emerald-400 to-green-500', textColor: 'text-emerald-600' },
};

const CertificateView = () => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const certificateRef = useRef(null);
  
  const [certificate, setCertificate] = useState(null);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (certificateId) fetchCertificate();
  }, [certificateId]);
  
  const fetchCertificate = async () => {
    try {
      setLoading(true);
      const response = await getCertificate(certificateId);
      if (response.success) setCertificate(response.data);
    } catch (error) {
      toast.error('Certificate not found');
      navigate('/my-learning');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePrint = () => window.print();
  
  const handleShare = async () => {
    const url = `${window.location.origin}/verify-certificate/${certificate.certificate_number}`;
    if (navigator.share) {
      try { await navigator.share({ title: `Certificate: ${certificate.course_title}`, text: `I completed ${certificate.course_title}!`, url }); }
      catch (error) { copyToClipboard(url); }
    } else { copyToClipboard(url); }
  };
  
  const copyToClipboard = (text) => { navigator.clipboard.writeText(text); toast.success('Link copied!'); };
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Get template config
  const getTemplate = () => {
    const templateId = certificate?.certificate_template || 'classic';
    return CERTIFICATE_TEMPLATES[templateId] || CERTIFICATE_TEMPLATES.classic;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="h-12 w-12 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (!certificate) return null;

  const template = getTemplate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-4xl mx-auto">
          {/* Actions */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 print:hidden">
            <Button variant="ghost" onClick={() => navigate('/my-learning')}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" onClick={handleShare} className="flex-1 sm:flex-none"><Share2 className="w-4 h-4 mr-2" />Share</Button>
              <Button variant="outline" onClick={handlePrint} className="flex-1 sm:flex-none"><Printer className="w-4 h-4 mr-2" />Print</Button>
              {certificate.pdf_url && <Button className="bg-indigo-600 hover:bg-indigo-700 flex-1 sm:flex-none" onClick={() => window.open(certificate.pdf_url, '_blank')}><Download className="w-4 h-4 mr-2" />PDF</Button>}
            </div>
          </div>
          
          {/* Certificate - Uses template colors */}
          <div ref={certificateRef} className="bg-white rounded-lg shadow-2xl overflow-hidden print:shadow-none">
            <div className={`bg-gradient-to-r ${template.gradient} h-3 sm:h-4`} />
            <div className="p-6 sm:p-8 md:p-12 text-center">
              {/* Company Logo */}
              <div className="mb-6 sm:mb-8">
                <img 
                  src="https://customer-assets.emergentagent.com/job_nerdstudio/artifacts/4gelybpg_nerd%20indians%20%28200%20x%20200%20px%29%281%29.png" 
                  alt="Nerd Indians"
                  className="w-24 h-24 sm:w-28 sm:h-28 md:w-32 md:h-32 mx-auto object-contain" loading="lazy" decoding="async"/>
              </div>
              
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-serif text-gray-800 mb-2">Certificate of Completion</h1>
              <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-base">This is to certify that</p>
              
              <h2 className={`text-xl sm:text-2xl md:text-3xl font-bold ${template.textColor} mb-2 font-serif`}>{certificate.recipient_name}</h2>
              <p className="text-gray-500 mb-6 sm:mb-8 text-sm sm:text-base">has successfully completed</p>
              
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-800 mb-2">{certificate.course_title}</h3>
              {certificate.certificate_custom_text && (
                <p className="text-gray-600 italic text-sm sm:text-base mb-2">{certificate.certificate_custom_text}</p>
              )}
              {certificate.batch_name && <p className="text-gray-500 mb-6 sm:mb-8 text-sm">Batch: {certificate.batch_name}</p>}
              
              <div className="flex justify-center mb-6 sm:mb-8">
                <div className="text-center">
                  <Calendar className={`w-5 h-5 sm:w-6 sm:h-6 mx-auto ${template.textColor} mb-1`} />
                  <p className="text-xs sm:text-sm text-gray-500">Date</p>
                  <p className="font-semibold text-sm sm:text-base">{formatDate(certificate.issued_at)}</p>
                </div>
              </div>
              
              <div className="mb-6 sm:mb-8 p-3 sm:p-4 bg-gray-50 rounded-lg inline-block">
                <p className="text-xs text-gray-500">Certificate ID</p>
                <p className="font-mono text-sm sm:text-lg font-bold text-gray-700">{certificate.certificate_number}</p>
              </div>
              
              <div className="text-xs sm:text-sm text-gray-500">
                <p>Verify at:</p>
                <p className={`font-mono ${template.textColor} break-all`}>{window.location.origin}/verify-certificate/{certificate.certificate_number}</p>
              </div>
            </div>
            <div className={`bg-gradient-to-r ${template.gradient} h-3 sm:h-4`} />
          </div>
          
          {/* Info Card */}
          <Card className="mt-6 print:hidden">
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center gap-2 text-green-600 mb-4">
                <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5" />
                <span className="font-medium text-sm sm:text-base">Verified Certificate</span>
              </div>
              <div className="grid grid-cols-2 gap-3 sm:gap-4 text-xs sm:text-sm">
                <div><p className="text-gray-500">Issued To</p><p className="font-medium">{certificate.recipient_name}</p></div>
                <div><p className="text-gray-500">Issued On</p><p className="font-medium">{formatDate(certificate.issued_at)}</p></div>
                <div><p className="text-gray-500">Course</p><p className="font-medium">{certificate.course_title}</p></div>
                <div><p className="text-gray-500">Certificate #</p><p className="font-medium font-mono text-xs">{certificate.certificate_number}</p></div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

// Certificate Verification Page
export const CertificateVerify = () => {
  const navigate = useNavigate();
  const [certificateNumber, setCertificateNumber] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [result, setResult] = useState(null);
  
  const handleVerify = async () => {
    if (!certificateNumber.trim()) { toast.error('Enter a certificate number'); return; }
    try {
      setVerifying(true);
      const response = await verifyCertificate(certificateNumber.trim());
      setResult(response.data);
    } catch (error) { setResult({ valid: false }); }
    finally { setVerifying(false); }
  };
  
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-50 via-white to-amber-50/30">
      <div className="p-4 sm:p-6 lg:p-8">
        <div className="max-w-xl mx-auto">
          <div className="text-center mb-8">
            <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center mb-4">
              <Award className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">Verify Certificate</h1>
            <p className="text-gray-500 text-sm sm:text-base">Enter a certificate number to verify</p>
          </div>
          
          <Card className="shadow-xl">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row gap-2 mb-6">
                <Input placeholder="Enter certificate number" value={certificateNumber} onChange={(e) => setCertificateNumber(e.target.value)} onKeyPress={(e) => e.key === 'Enter' && handleVerify()} className="font-mono flex-1" />
                <Button className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto" onClick={handleVerify} disabled={verifying}>
                  <Search className="w-4 h-4 mr-2" />Verify
                </Button>
              </div>
              
              {result && (
                <div className={`p-4 sm:p-6 rounded-lg ${result.valid ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                  {result.valid ? (
                    <>
                      <div className="flex items-center gap-2 text-green-700 mb-4">
                        <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6" />
                        <span className="text-base sm:text-lg font-bold">Certificate Verified!</span>
                      </div>
                      <div className="space-y-2 sm:space-y-3 text-xs sm:text-sm">
                        <div className="flex items-center gap-2"><User className="w-4 h-4 text-gray-400" /><span className="text-gray-600">Recipient:</span><span className="font-medium">{result.recipient_name}</span></div>
                        <div className="flex items-center gap-2"><BookOpen className="w-4 h-4 text-gray-400" /><span className="text-gray-600">Course:</span><span className="font-medium">{result.course_title}</span></div>
                        <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-gray-400" /><span className="text-gray-600">Issued:</span><span className="font-medium">{formatDate(result.issued_at)}</span></div>
                        {result.grade && <div className="flex items-center gap-2"><Award className="w-4 h-4 text-gray-400" /><span className="text-gray-600">Grade:</span><span className="font-medium">{result.grade}</span></div>}
                      </div>
                    </>
                  ) : (
                    <div className="text-center text-red-700">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto bg-red-100 rounded-full flex items-center justify-center mb-3"><span className="text-xl sm:text-2xl">!</span></div>
                      <p className="font-bold">Certificate Not Found</p>
                      <p className="text-xs sm:text-sm mt-1">This certificate is not valid or has been revoked.</p>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
          
          <div className="text-center mt-6">
            <Button variant="link" onClick={() => navigate('/')}>Go to Homepage</Button>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default CertificateView;
